import React, { useEffect, useRef, useState } from "react";
import { ProgressBar } from "react-bootstrap";
import { Input } from "antd";
import swal from "sweetalert";
import { ImCross } from "react-icons/im";
import { AiOutlinePlus } from "react-icons/ai";
import { MAX_OWNER_SILK_IMAGES } from "./ownerSilkImagesApi";

const allowedMimeTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
];
const maxFileSizeBytes = 200 * 1024;

const OwnerSilkImageManagerModal = ({
  images,
  setImages,
  onUpload,
  onDeleteRemote,
  uploadProgress,
  uploadInProgress,
  onDeleteInProgressChange,
}) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const hiddenFileInputRef = useRef(null);

  useEffect(() => {
    if (onDeleteInProgressChange) {
      onDeleteInProgressChange(!!deletingImageId);
    }
  }, [deletingImageId, onDeleteInProgressChange]);

  const validateFiles = (files) => {
    if (!files.length) return false;
    if (images.length + files.length > MAX_OWNER_SILK_IMAGES) {
      swal("Error!", `You can upload maximum ${MAX_OWNER_SILK_IMAGES} images.`, "error");
      return false;
    }

    for (const file of files) {
      if (!allowedMimeTypes.includes(file.type)) {
        swal("Error!", "Only JPG, PNG and WEBP files are allowed.", "error");
        return false;
      }
      if (file.size > maxFileSizeBytes) {
        swal("Error!", "Each image must be 200KB or smaller.", "error");
        return false;
      }
    }
    return true;
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!validateFiles(selectedFiles)) return;

    try {
      setUploadingCount(selectedFiles.length);
      const uploaded = await onUpload(selectedFiles);
      setImages((prev) => {
        const hasDefault = prev.some((item) => item.isDefault);
        const next = uploaded.map((item, index) => ({
          _id: item._id,
          image: item.OwnerSilkColor || item.image,
          DescriptionEn: "",
          DescriptionAr: "",
          isDefault: !hasDefault && index === 0,
          isTemporary: true,
        }));
        return [...prev, ...next];
      });
    } finally {
      setUploadingCount(0);
    }
  };

  const updateDescription = (idx, key, value) => {
    setImages((prev) =>
      prev.map((item, currentIdx) =>
        currentIdx === idx ? { ...item, [key]: value } : item
      )
    );
  };

  const setDefaultImage = (idx) => {
    setImages((prev) =>
      prev.map((item, currentIdx) => ({ ...item, isDefault: currentIdx === idx }))
    );
  };

  const removeImage = async (idx) => {
    const target = images[idx];
    if (deletingImageId) return;
    try {
      setDeletingImageId(target?._id || `${target?.image}-${idx}`);
      if (target?._id && onDeleteRemote) {
        await onDeleteRemote(target._id);
      }
      setImages((prev) => {
        const filtered = prev.filter((_, i) => i !== idx);
        if (filtered.length === 0) return filtered;
        if (filtered.some((img) => img.isDefault)) return filtered;
        return filtered.map((img, i) =>
          i === 0 ? { ...img, isDefault: true } : img
        );
      });
    } finally {
      setDeletingImageId(null);
    }
  };

  return (
    <div className="ownerSilkInlineSection">
      <div className="ownerSilkUploadSection">
        <input
          ref={hiddenFileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          onChange={handleFileUpload}
          className="ownerSilkHiddenFileInput"
        />
        {(uploadInProgress || uploadingCount > 0) && (
          <div className="ownerSilkProgressWrap">
            <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
          </div>
        )}
        {deletingImageId && (
          <div className="ownerSilkProgressWrap">
            <ProgressBar now={100} striped animated label="Deleting..." />
          </div>
        )}
      </div>

      <div className="ownerSilkImageGrid">
        {images.map((item, idx) => (
          <div key={item._id || `${item.image}-${idx}`} className="ownerSilkCard">
            <div className="ownerSilkPreviewWrap">
              <img src={item.image} alt="owner silk" className="ownerSilkPreview" />
              <ImCross
                className="crossIcon"
                style={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  cursor: deletingImageId ? "not-allowed" : "pointer",
                  opacity:
                    deletingImageId === (item._id || `${item.image}-${idx}`)
                      ? 0.5
                      : 1,
                  pointerEvents: deletingImageId ? "none" : "auto",
                }}
                onClick={() => !deletingImageId && removeImage(idx)}
              />
            </div>
            <div className="ownerSilkActionWrap">
              <button
                type="button"
                className={
                  item.isDefault ? "SubmitButton ownerSilkDefaultBtn" : "ownerSilkSecondaryBtn"
                }
                onClick={() => setDefaultImage(idx)}
              >
                {item.isDefault ? "Default Image" : "Set as Default"}
              </button>
            </div>
            <div className="ownerSilkDescriptionWrap">
              <label className="ownerSilkDescriptionLabel">Description (English)</label>
              <Input
                className="ownerSilkDescriptionInput"
                value={item.DescriptionEn || ""}
                onChange={(e) => updateDescription(idx, "DescriptionEn", e.target.value)}
              />
            </div>
            <div className="ownerSilkDescriptionWrap">
              <label className="ownerSilkDescriptionLabel ownerSilkDescriptionLabelAr">
                الوصف (العربية)
              </label>
              <Input
                className="ownerSilkDescriptionInput ownerSilkArabicInput"
                value={item.DescriptionAr || ""}
                onChange={(e) => updateDescription(idx, "DescriptionAr", e.target.value)}
              />
            </div>
          </div>
        ))}
        {images.length < MAX_OWNER_SILK_IMAGES && (
          <button
            type="button"
            className="ownerSilkAddTile"
            onClick={() => hiddenFileInputRef.current?.click()}
          >
            <AiOutlinePlus />
            <span>Add Image</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OwnerSilkImageManagerModal;
